/**
 * Appointments — the dealership diary.
 *
 * Four views (WEEK / DAY / RANGE / LIST), multi-select filtering, a booking
 * modal with conflict override, and a side drawer with status transitions
 * driven by the legal lifecycle:
 *   REQUESTED  → Confirm / Cancel   (AI-booked: a single OK → CONFIRMED)
 *   CONFIRMED  → Check in / No-show / Cancel / Reschedule
 *   CHECKED_IN → Complete (with outcome code)
 *
 * Four things changed after the September dealer showcase:
 *
 * 1. Filters are multi-select. "I want test drives AND just my deliveries, and
 *    I can't have that — you can only single click." Selecting nothing means
 *    everything, which is what a dealer expects from a filter row.
 * 2. Type drives the card colour, status drives the left border. Test drives
 *    and deliveries used to be pixel-identical, distinguishable only by
 *    reading the label — and telling them apart at a glance is the whole job
 *    of this screen.
 * 3. Filtering happens server-side. The old client-side filter could only ever
 *    narrow the rows the diary had already returned, so it silently dropped
 *    anything past that.
 * 4. Every date boundary goes through the org-timezone helpers. The week used
 *    to be computed in the *browser's* zone and sent as the fetch window while
 *    the grid was drawn in the *dealership's* — so an interstate viewer saw a
 *    window an hour out of step with the days it was rendering.
 *
 * Filter selections persist per browser: staff work the same view every day and
 * rebuilding it each morning is exactly the friction this screen exists to remove.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatInOrgTz, partsInOrgTz, orgHourFraction, isOrgSameDay,
  orgWallClockToISO, orgTimezoneAbbrev,
  orgCivilToday, orgAddDays, orgStartOfWeek, orgRange,
  civilToISODate, isoDateToCivil, isOrgToday, orgDaysBetween,
} from '../lib/datetime';
import TopBar from '../components/TopBar';
import LookupField from '../components/LookupField';
import MultiSelectFilter, { selectionKey, selectionParam } from '../components/MultiSelectFilter';
import PresetsDropdown from '../components/PresetsDropdown';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import {
  APPT_TYPES, APPT_STATUSES, SALES_TYPES,
  typeMeta, statusLabel, statusColor,
  CALENDAR_PRESETS, assignConsultantColors, UNASSIGNED_COLOR,
} from '../lib/appointmentMeta';
import { appointmentsService } from '../lib/services';
import { useApi } from '../lib/hooks/useApi';
import { useToast } from '../lib/toast';
import { useCurrentUser } from '../lib/currentUser';
import { apiErrorMessage, apiErrorCode } from '../lib/apiError';
import { useConsultantOptions, useCustomerOptions, useLeadOptions, useVehicleOptions } from '../lib/useDirectory';
import { useDirectoryNames } from '../lib/useDirectoryNames';
import TestDriveCheckInModal from '../components/testdrive/TestDriveCheckInModal';
import SlotPicker, { todayISO } from '../components/SlotPicker';

const HOUR_START = 7;
const HOUR_END = 19;
// Row height per hour on the Day/Week grid. Bumped from 44 → 60 so a
// standard 60-minute appointment card holds all three lines (time + who,
// type + consultant, vehicle) without the bottom line being clipped.
// 30-minute cards still show two — my earlier "icon on line 1" change
// keeps the type visible even when the slot is short.
const HOUR_PX = 60;
const MAX_RANGE_DAYS = 31;
const FILTERS_KEY = 'spectraiq.diary.filters';

/**
 * Retained for the drawer and the booking modal, which are keyed off these
 * exact strings. The authoritative lists now live in lib/appointmentMeta.js —
 * one place, imported by every screen, so the enum can't drift again.
 */
const TYPE_OPTIONS = APPT_TYPES.map((t) => [t.value, t.label]);
const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS);
const STATUS_VAR = Object.fromEntries(APPT_STATUSES.map((s) => [s.value, s.color]));

const OUTCOME_OPTIONS = ['SOLD', 'QUOTED', 'FOLLOW_UP', 'NO_SALE'];
// Readable labels for the stored outcome code. DEF-117: the detail panel showed
// the raw enum ("NO_SALE") rather than "No sale".
const OUTCOME_LABEL = {
  SOLD: 'Sold', QUOTED: 'Quoted', FOLLOW_UP: 'Follow up', NO_SALE: 'No sale',
};
const outcomeLabel = (code) => OUTCOME_LABEL[code] || (code ? code.replace(/_/g, ' ') : '');

/* ── "AI Booked" badge ──────────────────────────────────────────────
   Appointments mirrored from an AI voice/chat booking carry source === 'AI'
   (Appointment.Source.AI, stamped in AppointmentService.createFromBooking).
   Surfacing it tells staff at a glance which diary entries the agent made —
   the "clearly tag appointments as AI Booked" requirement. Renders nothing
   for HUMAN / SELF_SERVE sources, so it is safe to drop in anywhere. */
function AiBadge({ source, compact = false, style }) {
  if (source !== 'AI') return null;
  return (
    <span
      className="pill"
      title="Booked automatically by the AI agent"
      style={{
        borderColor: 'var(--accent-primary)',
        color: 'var(--accent-primary)',
        background: 'color-mix(in srgb, var(--accent-primary) 14%, transparent)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      🤖 {compact ? 'AI' : 'AI Booked'}
    </span>
  );
}

/* ── formatting ── */
function fmtDay(d) {
  return formatInOrgTz(d, { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtTime(iso) {
  return formatInOrgTz(iso, { hour: '2-digit', minute: '2-digit' });
}
function fmtWhen(iso) {
  return formatInOrgTz(iso, {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}
function asList(d) {
  if (Array.isArray(d)) return d;
  return d?.content ?? d?.appointments ?? [];
}

/* ── persisted filters ── */
function loadFilters() {
  try {
    const raw = window.localStorage.getItem(FILTERS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return {
      types: new Set(o.types || []),
      statuses: new Set(o.statuses || []),
      consultantIds: new Set(o.consultantIds || []),
      locationIds: new Set(o.locationIds || []),
      selectedPresetIds: new Set(o.selectedPresetIds || []),
      view: o.view || 'WEEK',
    };
  } catch {
    // Private mode, cleared storage, or a stale shape — a filter row is not
    // worth failing a page render over.
    return null;
  }
}

function saveFilters(f) {
  try {
    window.localStorage.setItem(FILTERS_KEY, JSON.stringify({
      types: [...f.types],
      statuses: [...f.statuses],
      consultantIds: [...f.consultantIds],
      locationIds: [...f.locationIds],
      selectedPresetIds: [...f.selectedPresetIds],
      view: f.view,
    }));
  } catch { /* ignore */ }
}

/* ── mock fallback: 8 varied appointments spread across the current week ── */
function mkMock() {
  const mon = orgStartOfWeek(orgCivilToday());
  const at = (dayOff, h, m = 0) =>
    orgWallClockToISO(civilToISODate(orgAddDays(mon, dayOff)), `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  const mk = (id, dayOff, h, durH, type, status, consultant, customer, extra = {}) => ({
    id, type, status,
    startsAt: at(dayOff, h),
    endsAt: at(dayOff, h + durH),
    consultantId: consultant.toLowerCase().replace(/\s+/g, '-'),
    consultantName: consultant,
    customerName: customer,
    location: 'Showroom',
    ...extra,
  });
  // Every type here is a real Appointment.Type value. The old mock still
  // emitted the retired 'SALES' and 'FINANCE', which rendered as raw text and
  // made a broken enum look like a styling bug.
  return [
    mk('apt-1', 0, 9, 1, 'SHOWROOM_VISIT', 'CONFIRMED', 'Mia Chen', 'Harry Nguyen', { leadId: 'lead-101', notes: 'Interested in RAV4 Cruiser' }),
    mk('apt-2', 0, 14, 1, 'TEST_DRIVE', 'REQUESTED', 'Josh Patel', 'Sofia Rossi', { leadId: 'lead-102', location: 'Front lot' }),
    mk('apt-3', 1, 10, 2, 'DELIVERY', 'CONFIRMED', 'Mia Chen', 'Liam O’Brien', { customerId: 'cust-77', location: 'Delivery bay' }),
    mk('apt-4', 2, 11, 1, 'APPRAISAL', 'CHECKED_IN', 'Grace Kim', 'Noah Williams', { notes: 'Trade-in: 2019 Mazda CX-5' }),
    mk('apt-5', 2, 16, 1, 'FINANCE_CONSULT', 'REQUESTED', 'Josh Patel', 'Ava Taylor', { location: 'Finance office' }),
    mk('apt-6', 3, 9, 1, 'SHOWROOM_VISIT', 'COMPLETED', 'Grace Kim', 'Oliver Brown', { outcomeCode: 'QUOTED' }),
    mk('apt-7', 4, 13, 2, 'TEST_DRIVE', 'CONFIRMED', 'Mia Chen', 'Isla Martin', { leadId: 'lead-108' }),
    mk('apt-8', 5, 10, 1, 'DELIVERY', 'NO_SHOW', 'Josh Patel', 'Jack Wilson', {}),
  ];
}
const MOCK_APPOINTMENTS = mkMock();

/* ── hover popover ──────────────────────────────────────────────────────
   Replaces the OS `title=` tooltip on cards. Native tooltips were the
   default only because building a proper one is fiddly, and the price was
   that the diary's information density lived behind a 500ms browser delay
   in the system font. This one appears on hover, carries the full picture
   (type, status, when, who, car, location, notes), and is measured at open
   time so it flips above the card near the bottom of the grid and left of
   it near the right edge instead of being clipped by the pane.
*/
const POPOVER_W = 280;
const POPOVER_MARGIN = 8;

function AppointmentHoverCard({ appt, anchorRect, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  // Position after paint so we can measure our own height; a hover card that
  // guesses its size then flips visibly the next frame is the tooltip's other
  // ancient annoyance.
  useEffect(() => {
    if (!ref.current || !anchorRect) return;
    const h = ref.current.offsetHeight;
    const w = POPOVER_W;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer to the right of the card, flip left if it would overflow.
    let left = anchorRect.right + POPOVER_MARGIN;
    if (left + w + POPOVER_MARGIN > vw) {
      left = Math.max(POPOVER_MARGIN, anchorRect.left - w - POPOVER_MARGIN);
    }
    // Align top with the card; nudge upward if it would spill below.
    let top = anchorRect.top;
    if (top + h + POPOVER_MARGIN > vh) {
      top = Math.max(POPOVER_MARGIN, vh - h - POPOVER_MARGIN);
    }
    setPos({ top, left });
  }, [anchorRect]);

  if (!appt || !anchorRect) return null;
  const t = typeMeta(appt.type);
  const sColor = statusColor(appt.status);

  const row = (label, value) => value ? (
    <div style={{ display: 'flex', gap: 10, fontSize: 12, lineHeight: 1.5 }}>
      <span style={{ width: 70, flexShrink: 0, color: 'var(--text-tertiary)' }}>{label}</span>
      <span style={{ flex: 1, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      role="tooltip"
      onMouseLeave={onClose}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: POPOVER_W,
        zIndex: 1000,
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: 8,
        boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.18))',
        padding: '10px 12px',
        pointerEvents: 'auto',
        // Hidden until measured to avoid the flip-into-place flash.
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {/* Header — type on the left, status pill on the right. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 15 }} aria-hidden>{t.icon}</span>
        <span style={{ fontWeight: 700, color: t.color, flex: 1, fontSize: 13 }}>{t.label}</span>
        <AiBadge source={appt.source} compact style={{ fontSize: 10, padding: '0 5px' }} />
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
          padding: '2px 6px', borderRadius: 4,
          background: `color-mix(in srgb, ${sColor} 18%, transparent)`,
          color: sColor,
        }}>{statusLabel(appt.status)}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {row('When', `${fmtWhen(appt.startsAt)} – ${fmtTime(appt.endsAt)}`)}
        {row('Customer', appt._who)}
        {row('Consultant', appt._consultant)}
        {row('Vehicle', appt._vehicle)}
        {row('Location', appt.location)}
        {row('Notes', appt.notes)}
      </div>

      <div className="mono text-xs text-muted" style={{ marginTop: 10, opacity: 0.8 }}>
        Click the card to open
      </div>
    </div>
  );
}

/* ── card ── */
function ApptCard({ appt, onClick, positioned, style, consultantColor = UNASSIGNED_COLOR, hideTime = false }) {
  const t = typeMeta(appt.type);
  const sColor = statusColor(appt.status);
  const dimmed = appt.status === 'CANCELLED' || appt.status === 'NO_SHOW';
  // Hover popover: anchor is the card's bounding rect at hover-open time;
  // clearing it hides the popover.
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
        onMouseEnter={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
        onMouseLeave={() => setAnchor(null)}
        onFocus={(e) => setAnchor(e.currentTarget.getBoundingClientRect())}
        onBlur={() => setAnchor(null)}
        style={{
          ...(positioned ? { position: 'absolute', left: 3, right: 3 } : {}),
          ...style,
          // Consultant tints the card, status marks the edge, and the type is
          // carried by the icon + label on the lines inside.
          background: `color-mix(in srgb, ${consultantColor} 28%, var(--bg-secondary))`,
          border: '1px solid var(--border-color)',
          borderLeft: `4px solid ${sColor}`,
          borderRadius: 8,
          // Roomier than the original 4px/7px, fontSize 11 layout: at 30-minute
          // heights every card had two lines clipped off the bottom (the fix
          // that put the icon on line 1 was a workaround for it, not a cure).
          // A larger card lets all three lines land — the client asked to keep
          // the fuller look after seeing it in the design mocks.
          padding: '6px 10px',
          fontSize: 12,
          lineHeight: 1.45,
          cursor: 'pointer',
          overflow: 'hidden',
          opacity: dimmed ? 0.55 : 1,
          textDecoration: appt.status === 'CANCELLED' ? 'line-through' : 'none',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        {/*
          Line 1 leads with the type icon and label — a 30-minute slot in Week
          view is only ~22px tall, so overflow: hidden clips everything below
          this line. Before, line 1 was time+customer and the "🚗 Drive · Josh"
          line lived on line 2 and vanished the moment the card was short — so
          the whole grid became a wall of blue rectangles you had to click into
          to know a test drive from a delivery. The type is exactly the question
          this screen exists to answer at a glance (comment above), so it stays
          visible whatever the height.
        */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-primary)', fontWeight: 700 }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span aria-hidden>{t.icon}</span>
            {hideTime ? ` ${appt._who || '—'}` : ` ${fmtTime(appt.startsAt)} · ${appt._who || '—'}`}
          </span>
          <AiBadge source={appt.source} compact style={{ flexShrink: 0, fontSize: 10, padding: '0 5px' }} />
        </div>
        <div className="text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {t.short} · {appt._consultant || '—'}
        </div>
        {/*
          Third line is always rendered — an em-dash placeholder when there is
          no vehicle. This keeps the card at a consistent three-line height so
          neighbouring cards align on the grid, and it tells the reader "no car
          attached yet" at a glance (the drive attaches its car at check-in).
        */}
        <div className="text-muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {appt._vehicle || '—'}
        </div>
      </div>
      <AppointmentHoverCard appt={appt} anchorRect={anchor} onClose={() => setAnchor(null)} />
    </>
  );
}

/* ── booking modal ── */
function NewAppointmentModal({ onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    type: 'TEST_DRIVE',
    consultantId: '', leadId: '', customerId: '', vehicleId: '',
    location: 'Showroom', notes: '',
  });
  // The time comes from a bookable diary slot, exactly as it does when booking
  // from a lead — free-typed times were the one door where the diary's
  // schedules, blocks and existing bookings had no say (and the source of
  // every accidental double-book this modal's "force" flow existed to paper
  // over). Each slot carries its consultantId.
  const [date, setDate] = useState(todayISO());
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* Lookup sources. */
  const consultantsQ = useConsultantOptions();
  const customersQ = useCustomerOptions();
  // Only demo cars can be test-driven — a customer's actual new-car stock
  // and a used trade-in are not the fleet you put test-drive km on. Same
  // filter the Test Drives page's + New modal uses, so the two forms offer
  // the same list. Other appointment types (delivery, finance, visit, …)
  // still see every vehicle because they need to point at the customer's
  // ordered car, not a demo.
  const vehiclesQ = useVehicleOptions(
    form.type === 'TEST_DRIVE' ? { condition: 'DEMO' } : {}
  );
  // Leads render as "<customer name> · <vehicle>", so they need the customer
  // index to resolve the name.
  const customersById = useMemo(
    () => new Map(customersQ.options.map(o => [o.id, o])),
    [customersQ.options],
  );
  const leadsQ = useLeadOptions(customersById);

  /**
   * Picking a lead pins its customer too — a lead already knows who it belongs
   * to, and letting the two disagree is how you get an appointment attached to
   * the wrong person.
   */
  const onPickLead = (leadId, lead) => {
    setForm((f) => ({
      ...f,
      leadId: leadId || '',
      customerId: lead?.customerId || (leadId ? f.customerId : ''),
    }));
  };

  const leadPinsCustomer = !!(
    form.leadId && leadsQ.options.find(l => l.id === form.leadId)?.customerId
  );

  const submit = async (force) => {
    if (!slot) { toast('Pick an available slot', { variant: 'warning' }); return; }
    if (form.type === 'TEST_DRIVE' && !form.customerId && !form.leadId) {
      // The Test Drives register requires a customer on every drive, so a
      // customer-less test-drive booking would book fine here and then never
      // appear there — the exact mismatch this modal must not produce.
      toast('A test drive needs a lead or customer', { variant: 'warning' });
      return;
    }
    setBusy(true);
    try {
      const payload = {
        type: form.type,
        startsAt: slot.startsAt,
        endsAt: slot.endsAt,
        // The slot knows whose diary it came from; an explicitly chosen
        // consultant just narrows which slots were on offer.
        consultantId: form.consultantId || slot.consultantId,
        leadId: form.leadId || null,
        customerId: form.customerId || null,
        vehicleId: form.vehicleId || null,
        location: form.location.trim() || null,
        notes: form.notes.trim() || null,
        source: 'HUMAN', // → backend default status CONFIRMED
        ...(force ? { force: true } : {}),
      };
      await appointmentsService.create(payload);
      toast('Appointment booked', { variant: 'success' });
      onCreated();
      onClose();
    } catch (e) {
      if (apiErrorCode(e) === 'APPOINTMENT_CONFLICT') {
        setConflict(true);
        toast('That slot clashes with an existing appointment', { variant: 'warning' });
      } else {
        toast(`Booking failed: ${apiErrorMessage(e)}`, { variant: 'danger' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form
        className="modal" style={{ width: 'min(560px, 92vw)' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); submit(false); }}
      >
        <div className="modal-header">
          <div className="modal-title">New appointment</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="form-field">
            <label>Type</label>
            <select value={form.type} onChange={(e) => {
              // Type change refetches the stock list (TEST_DRIVE → DEMO only,
              // others → every condition). A vehicle chosen under the old
              // list may no longer be on the new one, so clear it — leaving a
              // stale non-DEMO id on a test drive would silently book with a
              // vehicle the picker never showed. Slot filtering is type-aware
              // too, so drop it for the same reason.
              setForm((f) => ({ ...f, type: e.target.value, vehicleId: '' }));
              setSlot(null);
            }}>
              {TYPE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
            </select>
          </div>

          <LookupField
            id="appt-consultant"
            label="Consultant (optional — narrows the slots)"
            placeholder="Search by name, email or role…"
            emptyHint="No consultant matches that search"
            options={consultantsQ.options}
            loading={consultantsQ.loading}
            error={consultantsQ.error}
            value={form.consultantId || null}
            onChange={(id) => { set('consultantId', id || ''); setConflict(false); }}
          />

          <SlotPicker
            date={date}
            onDateChange={setDate}
            type={form.type}
            consultantId={form.consultantId || undefined}
            value={slot}
            onSelect={(s) => { setSlot(s); setConflict(false); }}
            disabled={busy}
          />

          <LookupField
            id="appt-lead"
            label="Lead"
            placeholder="Search by customer, vehicle or stage…"
            emptyHint="No lead matches that search"
            options={leadsQ.options}
            loading={leadsQ.loading}
            value={form.leadId || null}
            onChange={onPickLead}
          />

          <LookupField
            id="appt-customer"
            label="Customer"
            placeholder="Search by name, phone or email…"
            emptyHint="No customer matches that search"
            options={customersQ.options}
            loading={customersQ.loading}
            value={form.customerId || null}
            onChange={(id) => set('customerId', id || '')}
            disabled={leadPinsCustomer}
          />
          {leadPinsCustomer && (
            <div className="mono text-xs text-muted" style={{ marginTop: -6 }}>
              Customer is taken from the selected lead. Clear the lead to choose a different one.
            </div>
          )}

          <LookupField
            id="appt-vehicle"
            label="Vehicle (optional)"
            placeholder="Search stock — attach at check-in if unsure"
            emptyHint="No vehicle matches that search"
            options={vehiclesQ.options}
            loading={vehiclesQ.loading}
            value={form.vehicleId || null}
            onChange={(id) => set('vehicleId', id || '')}
          />

          <div className="form-field">
            <label>Location</label>
            <input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Showroom" />
          </div>
          <div className="form-field">
            <label>Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
          {conflict && (
            <div style={{
              padding: '8px 12px', borderRadius: 6, fontSize: 12,
              border: '1px solid var(--warning)', color: 'var(--warning)',
              background: 'var(--warning-bg)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ flex: 1 }}>This slot conflicts with another appointment for the consultant.</span>
              <button type="button" className="btn small" disabled={busy} onClick={() => submit(true)}>
                Book anyway
              </button>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || !slot}>
            {busy ? 'Booking…' : 'Book appointment'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── detail drawer ── */
function ApptDrawer({ appt, onClose, onChanged }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState('SOLD');
  const [resched, setResched] = useState(false);
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  // Check-in (E13 / T000): licence photo, documents, on-screen signature and
  // the keys, in a step-by-step modal over the drawer.
  const [checkin, setCheckin] = useState(false);
  // Edit consultant: the dropdown lists ONLY consultants free and working for
  // this window (loaded from the backend when Edit is pressed).
  const [editingConsultant, setEditingConsultant] = useState(false);
  const [options, setOptions] = useState(null);       // null = loading
  const [pickedConsultant, setPickedConsultant] = useState('');

  if (!appt) return null;
  const color = STATUS_VAR[appt.status] || 'var(--text-tertiary)';

  // DEF-117: an appointment can't be checked in, completed or marked no-show
  // before the day it is booked for. Compare calendar DAYS in the org timezone
  // (a customer arriving early ON the booked day is fine; a future-dated one is
  // not). The backend enforces this too — this just keeps the drawer honest so
  // the user sees why rather than clicking into a 400.
  const _sp = partsInOrgTz(appt.startsAt);
  const _today = orgCivilToday();
  const dayKey = (y, m, d) => y * 10000 + m * 100 + d;
  const isFutureDate = !!_sp
    && dayKey(_sp.year, _sp.month, _sp.day)
       > dayKey(_today.getFullYear(), _today.getMonth() + 1, _today.getDate());

  const changeStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      await appointmentsService.updateStatus(appt.id, { status, ...extra });
      toast(`Appointment → ${status.replace('_', ' ')}`, { variant: 'success' });
      onChanged();
      onClose();
    } catch (e) {
      toast(`Update failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const doReschedule = async () => {
    if (!newStart || !newEnd) { toast('Pick both new times', { variant: 'warning' }); return; }
    setBusy(true);
    try {
      await appointmentsService.reschedule(appt.id, {
        // datetime-local values are dealership-local wall-clock, not browser-local.
        startsAt: orgWallClockToISO(newStart),
        endsAt: orgWallClockToISO(newEnd),
      });
      toast('Appointment rescheduled', { variant: 'success' });
      onChanged();
      onClose();
    } catch (e) {
      toast(`Reschedule failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const canEditConsultant = ['REQUESTED', 'CONFIRMED', 'RESCHEDULED'].includes(appt.status);

  const startEditConsultant = async () => {
    setEditingConsultant(true);
    setOptions(null);
    setPickedConsultant(appt.consultantId || '');
    try {
      const list = await appointmentsService.availableConsultants(appt.id);
      setOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      setOptions([]);
      toast(`Could not load available consultants: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const saveConsultant = async () => {
    if (!pickedConsultant || pickedConsultant === appt.consultantId) { setEditingConsultant(false); return; }
    setBusy(true);
    try {
      await appointmentsService.changeConsultant(appt.id, pickedConsultant);
      toast('Consultant updated', { variant: 'success' });
      onChanged();
      onClose();
    } catch (e) {
      toast(`Could not change consultant: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', gap: 10, fontSize: 13 }}>
      <span className="text-muted" style={{ width: 100, flexShrink: 0 }}>{label}</span>
      <span>{value || '—'}</span>
    </div>
  );

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }}
      />
      <aside style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(400px, 92vw)',
        background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-lg)', zIndex: 201, padding: 20,
        display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{appt._who && appt._who !== '—' ? appt._who : 'Appointment'}</div>
            <div className="mono text-xs text-muted">{TYPE_LABEL[appt.type] || appt.type} · {fmtWhen(appt.startsAt)}</div>
          </div>
          <button className="btn small" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span className="pill filled" style={{ color, alignSelf: 'flex-start' }}>{appt.status}</span>
          <AiBadge source={appt.source} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {row('Type', TYPE_LABEL[appt.type] || appt.type)}
          {row('Starts', fmtWhen(appt.startsAt))}
          {row('Ends', fmtWhen(appt.endsAt))}
          <div style={{ display: 'flex', gap: 10, fontSize: 13, alignItems: 'center' }}>
            <span className="text-muted" style={{ width: 100, flexShrink: 0 }}>Consultant</span>
            <span style={{ flex: 1 }}>{appt._consultant || '—'}</span>
            {canEditConsultant && !editingConsultant && (
              <button type="button" className="btn small" disabled={busy} onClick={startEditConsultant}>Edit</button>
            )}
          </div>
          {editingConsultant && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 110 }}>
              {options === null ? (
                <span className="text-muted text-xs">Loading available consultants…</span>
              ) : options.length === 0 ? (
                <span className="text-xs" style={{ color: 'var(--warning)' }}>
                  No consultant is free at this date and time.
                </span>
              ) : (
                <select value={pickedConsultant} disabled={busy}
                  onChange={(e) => setPickedConsultant(e.target.value)}>
                  {!options.some((o) => o.id === pickedConsultant) && (
                    <option value="">Select a consultant…</option>
                  )}
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn small primary"
                  disabled={busy || !pickedConsultant || pickedConsultant === appt.consultantId}
                  onClick={saveConsultant}>Save</button>
                <button type="button" className="btn small" disabled={busy}
                  onClick={() => setEditingConsultant(false)}>Cancel</button>
              </div>
              <span className="mono text-xs text-muted">Only consultants free at this time are listed.</span>
            </div>
          )}
          {row('Customer', appt._customer)}
          {appt._lead && row('Lead', appt._lead)}
          {appt._vehicle && row('Vehicle', appt._vehicle)}
          {row('Location', appt.location)}
          {row('Notes', appt.notes)}
          {appt.outcomeCode && row('Outcome', outcomeLabel(appt.outcomeCode))}
        </div>

        {/* ── Start test drive (E13 / T000) ──────────────────────────────
            This is the step that turns a BOOKING into a DRIVEN car. Until it
            existed the test_drives table was never written to, so every
            test-drive report read zero while the dashboard counted
            appointments — two numbers, one word.

            Offered on any open test-drive appointment regardless of status:
            the customer is standing at the desk, and making the consultant
            first press Confirm to make a Requested booking eligible is the
            kind of friction that ends with the register not being used. The
            backend walks the appointment through the legal transitions. */}
        {appt.type === 'TEST_DRIVE'
          && !['COMPLETED', 'CANCELLED'].includes(appt.status) && (
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
              <button className="btn small primary" style={{ width: '100%' }}
                disabled={busy || isFutureDate} onClick={() => setCheckin(true)}
                title={isFutureDate ? 'Opens on the day this drive is booked for' : undefined}>
                🔑 Start test drive
              </button>
              <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
                {isFutureDate
                  ? `Opens on ${fmtDay(appt.startsAt)} — the day this drive is booked for.`
                  : 'Licence photo · documents · customer signature · keys'}
              </div>
              {checkin && (
                <TestDriveCheckInModal
                  appointmentId={appt.id}
                  scheduledAt={appt.startsAt}
                  customerName={appt._customer || appt._who}
                  vehicleLabel={appt._vehicle}
                  onClose={() => setCheckin(false)}
                  onDone={() => { onChanged(); onClose(); }}
                />
              )}
            </div>
          )}

        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* DEF-117: future-dated appointment — attendance actions (check-in,
              no-show, complete) open on the booked day. Confirm, reschedule and
              cancel stay available so staff can still manage the booking. */}
          {isFutureDate && !['COMPLETED', 'NO_SHOW', 'CANCELLED'].includes(appt.status) && (
            <div style={{
              padding: '8px 12px', borderRadius: 6, fontSize: 12,
              border: '1px solid var(--warning)', color: 'var(--warning)',
              background: 'var(--warning-bg)', display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <span aria-hidden>📅</span>
              <span>
                Booked for <strong>{fmtDay(appt.startsAt)}</strong>. Check-in, no-show and
                completion open on the day — reschedule it to proceed earlier.
              </span>
            </div>
          )}
          {/* AI-booked: the customer already had the time confirmed in chat, so
              Confirm/Cancel is meaningless. One OK acknowledges it and moves it
              REQUESTED → CONFIRMED (Cancel/Reschedule appear once confirmed). */}
          {appt.status === 'REQUESTED' && appt.source === 'AI' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn small primary" disabled={busy} onClick={() => changeStatus('CONFIRMED')}>OK</button>
            </div>
          )}
          {appt.status === 'REQUESTED' && appt.source !== 'AI' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn small primary" disabled={busy} onClick={() => changeStatus('CONFIRMED')}>Confirm</button>
              <button className="btn small danger" disabled={busy} onClick={() => changeStatus('CANCELLED')}>Cancel</button>
            </div>
          )}
          {appt.status === 'CONFIRMED' && (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!isFutureDate && (
                  <button className="btn small primary" disabled={busy} onClick={() => changeStatus('CHECKED_IN')}>Check in</button>
                )}
                <button className="btn small" disabled={busy} onClick={() => setResched((v) => !v)}>Reschedule</button>
                {!isFutureDate && (
                  <button className="btn small danger" disabled={busy} onClick={() => changeStatus('NO_SHOW')}>No-show</button>
                )}
                <button className="btn small danger" disabled={busy} onClick={() => changeStatus('CANCELLED')}>Cancel</button>
              </div>
              {resched && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                  <div className="form-field">
                    <label>New start</label>
                    <input type="datetime-local" value={newStart} onChange={(e) => setNewStart(e.target.value)} />
                  </div>
                  <div className="form-field">
                    <label>New end</label>
                    <input type="datetime-local" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
                  </div>
                  <button className="btn small primary" disabled={busy} onClick={doReschedule}>Save new time</button>
                </div>
              )}
            </>
          )}
          {appt.status === 'CHECKED_IN' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div className="form-field" style={{ flex: 1 }}>
                <label>Outcome</label>
                <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                  {OUTCOME_OPTIONS.map((o) => <option key={o} value={o}>{outcomeLabel(o)}</option>)}
                </select>
              </div>
              <button
                className="btn small primary" disabled={busy || isFutureDate}
                title={isFutureDate ? 'Opens on the day this appointment is booked for' : undefined}
                onClick={() => changeStatus('COMPLETED', { outcomeCode: outcome })}
              >Complete</button>
            </div>
          )}
          {['COMPLETED', 'NO_SHOW', 'CANCELLED'].includes(appt.status) && (
            <div className="text-muted" style={{ fontSize: 12 }}>This appointment is closed — no further actions.</div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ── page ── */
export default function Appointments() {
  const persisted = useMemo(loadFilters, []);

  const [view, setView] = useState(persisted?.view || 'WEEK');
  // All navigation state is a civil date in the ORG zone — never a browser-local
  // midnight. See lib/datetime.js for why that distinction is not academic.
  const [anchor, setAnchor] = useState(() => orgCivilToday());
  const [rangeFrom, setRangeFrom] = useState(() => orgStartOfWeek(orgCivilToday()));
  const [rangeTo, setRangeTo] = useState(() => orgAddDays(orgStartOfWeek(orgCivilToday()), 6));

  const [types, setTypes] = useState(persisted?.types || new Set());
  const [statuses, setStatuses] = useState(persisted?.statuses || new Set());
  const [consultantIds, setConsultantIds] = useState(persisted?.consultantIds || new Set());
  const [locationIds, setLocationIds] = useState(persisted?.locationIds || new Set());
  const [selectedPresetIds, setSelectedPresetIds] = useState(persisted?.selectedPresetIds || new Set());
  // Service appointments do not belong on a sales diary. Off by default; a
  // service user flips it. (Sales/Service segregation — dealer feedback.)
  const [includeService, setIncludeService] = useState(false);

  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    saveFilters({ types, statuses, consultantIds, locationIds, selectedPresetIds, view });
  }, [types, statuses, consultantIds, locationIds, selectedPresetIds, view]);

  /* ── which days are we showing ── */
  const weekStart = useMemo(() => orgStartOfWeek(anchor), [anchor]);
  const days = useMemo(() => {
    if (view === 'DAY') return [anchor];
    if (view === 'RANGE') {
      const n = Math.min(MAX_RANGE_DAYS, Math.max(1, orgDaysBetween(rangeFrom, rangeTo) + 1));
      return Array.from({ length: n }, (_, i) => orgAddDays(rangeFrom, i));
    }
    return Array.from({ length: 7 }, (_, i) => orgAddDays(weekStart, i));
  }, [view, anchor, weekStart, rangeFrom, rangeTo]);

  const { fromISO: from, toISO: to } = useMemo(
    () => orgRange(days[0], days[days.length - 1]),
    [days],
  );

  const step = view === 'DAY' ? 1 : (view === 'RANGE' ? days.length : 7);
  const go = (dir) => {
    if (view === 'RANGE') {
      setRangeFrom((f) => orgAddDays(f, dir * step));
      setRangeTo((t) => orgAddDays(t, dir * step));
    } else {
      setAnchor((a) => orgAddDays(a, dir * step));
    }
  };
  const goToday = () => {
    const t = orgCivilToday();
    setAnchor(t);
    setRangeFrom(orgStartOfWeek(t));
    setRangeTo(orgAddDays(orgStartOfWeek(t), 6));
  };

  /* ── data ── */
  const effectiveTypes = useMemo(() => {
    if (types.size) return types;
    // "All" on a sales diary means all SALES types unless service is opted in.
    return includeService ? new Set() : new Set(SALES_TYPES);
  }, [types, includeService]);

  const { data, loading, error, refetch } = useApi(
    () => appointmentsService.diary({
      from, to,
      types: selectionParam(effectiveTypes),
      statuses: selectionParam(statuses),
      consultantIds: selectionParam(consultantIds),
      locationIds: selectionParam(locationIds),
    }),
    {
      devFallback: MOCK_APPOINTMENTS,
      deps: [from, to, selectionKey(effectiveTypes), selectionKey(statuses), selectionKey(consultantIds), selectionKey(locationIds)],
    },
  );

  // Filter options come from their own endpoint so a consultant with a quiet
  // week is still in the list. Falls back to whatever is loaded if the
  // endpoint isn't there yet.
  const { data: optionsData } = useApi(
    () => appointmentsService.filterOptions({ from, to }),
    { fallback: null, deps: [from, to] },
  );

  const names = useDirectoryNames();
  const { consultantId: myConsultantId } = useCurrentUser();

  // Enrich every diary row with resolved, human display fields so the cards,
  // list and drawer never fall back to a raw UUID. `_who` = customer name, or
  // the lead's label, or an em dash.
  const appts = useMemo(() => asList(data).map((a) => {
    const _customer = names.customer(a.customerId, a.customerName, { dash: null });
    const _lead = names.lead(a.leadId, a.leadName, { dash: null });
    const _consultant = names.consultant(a.consultantId, a.consultantName, { dash: null });
    const _vehicle = names.vehicle(a.vehicleId, a.vehicleLabel, { dash: null });
    return { ...a, _customer, _lead, _consultant, _vehicle, _who: _customer || _lead || '—' };
  }), [data, names]);

  // Belt and braces: the server filters, but the dev fallback and any backend
  // that predates the multi-value params do not. Re-applying the same
  // predicate client-side costs nothing and keeps the fallback honest.
  const filtered = useMemo(() => appts.filter((a) => {
    if (effectiveTypes.size && !effectiveTypes.has(a.type)) return false;
    if (statuses.size && !statuses.has(a.status)) return false;
    if (consultantIds.size && !consultantIds.has(a.consultantId)) return false;
    if (locationIds.size && a.locationId && !locationIds.has(a.locationId)) return false;
    return true;
  }), [appts, effectiveTypes, statuses, consultantIds, locationIds]);

  const consultantOptions = useMemo(() => {
    const fromApi = optionsData?.consultants;
    if (Array.isArray(fromApi) && fromApi.length) {
      return fromApi.map((c) => ({ value: c.id, label: c.name }));
    }
    const seen = new Map();
    appts.forEach((a) => { if (a.consultantId && !seen.has(a.consultantId)) seen.set(a.consultantId, a._consultant || a.consultantId); });
    return [...seen].map(([value, label]) => ({ value, label })).sort((x, y) => String(x.label).localeCompare(String(y.label)));
  }, [optionsData, appts]);

  // One colour per consultant, derived from their id and kept unique across the
  // consultants on screen, so a person keeps the same colour in every view.
  const consultantColors = useMemo(() => {
    const entries = new Map(consultantOptions.map((c) => [c.value, c.label]));
    appts.forEach((a) => { if (a.consultantId && !entries.has(a.consultantId)) entries.set(a.consultantId, a._consultant || a.consultantId); });
    const colors = assignConsultantColors([...entries.keys()]);
    return new Map([...entries].map(([id, label]) => [id, { label, color: colors.get(String(id)) }]));
  }, [consultantOptions, appts]);
  // Filter dropdown rows carry each consultant's colour next to their name.
  const consultantFilterOptions = useMemo(
    () => consultantOptions.map((c) => ({ ...c, swatch: consultantColors.get(c.value)?.color })),
    [consultantOptions, consultantColors],
  );
  // The legend only names the signed-in consultant; everyone's colour is in the dropdown.
  const myLegendEntry = myConsultantId && consultantColors.has(myConsultantId)
    ? [[myConsultantId, consultantColors.get(myConsultantId)]]
    : [];
  const colorFor = (a) => consultantColors.get(a.consultantId)?.color || UNASSIGNED_COLOR;
  const toggleConsultant = (id) => {
    const next = new Set(consultantIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setConsultantIds(next);
  };

  const locationOptions = useMemo(() => {
    const fromApi = optionsData?.locations;
    return Array.isArray(fromApi) ? fromApi.map((l) => ({ value: l.id, label: l.name })) : [];
  }, [optionsData]);

  const typeOptions = useMemo(
    () => APPT_TYPES
      .filter((t) => includeService || t.stream !== 'SERVICE')
      .map((t) => ({ value: t.value, label: t.short, icon: t.icon, color: t.color, title: t.label })),
    [includeService],
  );
  const statusOptions = useMemo(
    () => APPT_STATUSES.map((s) => ({ value: s.value, label: s.label, color: s.color })),
    [],
  );

  const togglePreset = (presetId) => {
    const nextIds = new Set(selectedPresetIds);
    if (nextIds.has(presetId)) nextIds.delete(presetId);
    else nextIds.add(presetId);
    setSelectedPresetIds(nextIds);

    const active = CALENDAR_PRESETS.filter((p) => nextIds.has(p.id));
    const mergedTypes = new Set();
    const mergedStatuses = new Set();
    const mergedConsultantIds = new Set();
    active.forEach((p) => {
      p.filters.types.forEach((t) => mergedTypes.add(t));
      p.filters.statuses.forEach((s) => mergedStatuses.add(s));
      p.filters.consultantIds.forEach((c) => mergedConsultantIds.add(c));
    });
    setTypes(mergedTypes);
    setStatuses(mergedStatuses);
    setConsultantIds(mergedConsultantIds);
  };
  const clearPresets = () => {
    setSelectedPresetIds(new Set());
    setTypes(new Set()); setStatuses(new Set()); setConsultantIds(new Set());
  };
  const clearAll = () => {
    setTypes(new Set()); setStatuses(new Set()); setConsultantIds(new Set()); setLocationIds(new Set());
    setSelectedPresetIds(new Set());
  };
  const activeFilterCount = types.size + statuses.size + consultantIds.size + locationIds.size;

  const hours = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

  // Match appointments to columns/rows in the DEALERSHIP timezone so the grid
  // agrees with the (org-zone) time labels — using the browser clock here is
  // what put a 1:44pm booking in the 9–10 row for an interstate viewer.
  const forDay = (day) => filtered
    .filter((a) => isOrgSameDay(a.startsAt, day))
    .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));

  const positionFor = (a) => {
    const startH = orgHourFraction(a.startsAt);
    if (startH == null) return { top: 0, height: 24 };
    // Fall back to a 30-min block when there's no end time.
    const endH = orgHourFraction(a.endsAt) ?? (startH + 0.5);
    const top = Math.max(0, (startH - HOUR_START) * HOUR_PX);
    const height = Math.max(24, (Math.min(endH, HOUR_END) - Math.max(startH, HOUR_START)) * HOUR_PX - 3);
    return { top, height };
  };

  // Side-by-side lanes for appointments that overlap in time. Overlapping
  // appointments form a cluster; each takes a column and the cluster's columns
  // share the width equally. Returns Map(id → { col, cols }).
  const lanesFor = (list) => {
    const items = list.map((a) => {
      const s = orgHourFraction(a.startsAt) ?? 0;
      const e = orgHourFraction(a.endsAt) ?? (s + 0.5);
      return { id: a.id, s, e: Math.max(e, s + 0.4) };
    }).sort((x, y) => x.s - y.s || x.e - y.e);
    const out = new Map();
    let cluster = [];
    let clusterEnd = -Infinity;
    const flush = () => {
      const colEnds = [];
      cluster.forEach((it) => {
        let c = colEnds.findIndex((end) => end <= it.s);
        if (c === -1) { c = colEnds.length; colEnds.push(it.e); } else colEnds[c] = it.e;
        it.col = c;
      });
      cluster.forEach((it) => out.set(it.id, { col: it.col, cols: colEnds.length }));
      cluster = [];
    };
    items.forEach((it) => {
      if (cluster.length && it.s >= clusterEnd) { flush(); clusterEnd = -Infinity; }
      cluster.push(it);
      clusterEnd = Math.max(clusterEnd, it.e);
    });
    if (cluster.length) flush();
    return out;
  };

  const rangeLabel = view === 'DAY'
    ? formatInOrgTz(anchor, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : `${fmtDay(days[0])} – ${fmtDay(days[days.length - 1])}`;

  return (
    <>
      <TopBar
        crumbs={['Appointments']}
        right={
          <>
            <button className="btn small" onClick={refetch}>↻ Refresh</button>
            <button className="btn small primary" onClick={() => setModalOpen(true)}>+ New appointment</button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Toolbar: view + navigation */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {['DAY', 'WEEK', 'RANGE', 'LIST'].map((v) => (
              <button
                key={v}
                className={`btn small ${view === v ? 'primary' : ''}`}
                onClick={() => setView(v)}
              >{v[0] + v.slice(1).toLowerCase()}</button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button className="btn small" onClick={() => go(-1)} aria-label="Previous">‹</button>
            <button className="btn small" onClick={goToday}>Today</button>
            <button className="btn small" onClick={() => go(1)} aria-label="Next">›</button>

            {view === 'DAY' && (
              <input
                type="date"
                value={civilToISODate(anchor)}
                onChange={(e) => { const c = isoDateToCivil(e.target.value); if (c) setAnchor(c); }}
                aria-label="Pick a day"
              />
            )}
            {view === 'RANGE' && (
              <>
                <input
                  type="date"
                  value={civilToISODate(rangeFrom)}
                  onChange={(e) => { const c = isoDateToCivil(e.target.value); if (c) { setRangeFrom(c); if (orgDaysBetween(c, rangeTo) < 0) setRangeTo(c); } }}
                  aria-label="Range start"
                />
                <span className="text-muted">to</span>
                <input
                  type="date"
                  value={civilToISODate(rangeTo)}
                  onChange={(e) => { const c = isoDateToCivil(e.target.value); if (c) { setRangeTo(c); if (orgDaysBetween(rangeFrom, c) < 0) setRangeFrom(c); } }}
                  aria-label="Range end"
                />
              </>
            )}
            <span className="mono text-xs text-muted" style={{ marginLeft: 6 }}>
              {rangeLabel} · {orgTimezoneAbbrev()}
            </span>
          </div>

          <span style={{ flex: 1 }} />
          {loading && <span className="mono text-xs text-muted">loading…</span>}
          {error && !loading && (
            <span className="mono text-xs" style={{ color: 'var(--danger)' }}>
              Couldn't load the diary — showing what we have.
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="card" style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="flex-row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="card-label">PRESETS</span>
            <PresetsDropdown
              presets={CALENDAR_PRESETS}
              selected={selectedPresetIds}
              onToggle={togglePreset}
              onClear={clearPresets}
            />
            <div className="flex-row" style={{ gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
              {activeFilterCount > 0 && (
                <button type="button" className="btn small" onClick={clearAll}>
                  Clear filters ({activeFilterCount})
                </button>
              )}
              <MultiSelectDropdown label="Status" options={statusOptions} value={statuses} onChange={setStatuses} />
              {consultantOptions.length > 0 && (
                <MultiSelectDropdown label="Consultant" options={consultantFilterOptions} value={consultantIds} onChange={setConsultantIds} />
              )}
            </div>
          </div>
          <MultiSelectFilter label="TYPE" options={typeOptions} value={types} onChange={setTypes} />
          {locationOptions.length > 1 && (
            <MultiSelectFilter label="LOCATION" options={locationOptions} value={locationIds} onChange={setLocationIds} allLabel="All sites" />
          )}
          <label className="mono text-xs text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeService} onChange={(e) => setIncludeService(e.target.checked)} />
            Include service appointments
          </label>
        </div>

        {/* Legend: you (card colour, click to filter), type (icon), status (left edge) */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {myLegendEntry.map(([id, c]) => {
            const on = consultantIds.has(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleConsultant(id)}
                title={on ? `Stop filtering to ${c.label}` : `Show only ${c.label}`}
                aria-pressed={on}
                className="mono text-xs"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                  background: 'transparent', border: 'none', padding: 0,
                  color: on ? 'var(--text-primary)' : 'var(--text-muted)',
                  fontWeight: on ? 700 : 400,
                }}
              >
                <span style={{ width: 10, height: 10, borderRadius: 2, background: c.color, display: 'inline-block' }} />
                {c.label}
              </button>
            );
          })}
          {myLegendEntry.length > 0 && <span style={{ width: 1, height: 14, background: 'var(--border-color)' }} />}
          {APPT_TYPES.filter((t) => includeService || t.stream !== 'SERVICE').map((t) => (
            <span key={t.value} className="mono text-xs text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              {t.icon} {t.short}
            </span>
          ))}
          <span style={{ width: 1, height: 14, background: 'var(--border-color)' }} />
          {APPT_STATUSES.map((s) => (
            <span key={s.value} className="mono text-xs text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 3, height: 12, background: s.color, display: 'inline-block' }} />
              {s.label}
            </span>
          ))}
        </div>

        {/* WEEK view */}
        {view === 'WEEK' && (
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '48px repeat(7, minmax(120px, 1fr))', minWidth: 900 }}>
              <div />
              {days.map((d) => (
                <div
                  key={civilToISODate(d)}
                  onClick={() => { setAnchor(d); setView('DAY'); }}
                  title="Open this day"
                  style={{
                    padding: '8px 6px', textAlign: 'center', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    borderBottom: '1px solid var(--border-subtle)',
                    borderLeft: '1px solid var(--border-subtle)',
                    color: isOrgToday(d) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  }}
                >{fmtDay(d)}</div>
              ))}
              {/* Hour gutter */}
              <div style={{ position: 'relative', height: (HOUR_END - HOUR_START) * HOUR_PX }}>
                {hours.map((h) => (
                  <div
                    key={h}
                    className="mono text-xs text-muted"
                    style={{ position: 'absolute', top: (h - HOUR_START) * HOUR_PX - 6, right: 6 }}
                  >{String(h).padStart(2, '0')}:00</div>
                ))}
              </div>
              {days.map((d) => (
                <div
                  key={`col-${civilToISODate(d)}`}
                  style={{
                    position: 'relative',
                    height: (HOUR_END - HOUR_START) * HOUR_PX,
                    borderLeft: '1px solid var(--border-subtle)',
                    background: isOrgToday(d) ? 'color-mix(in srgb, var(--accent-primary) 4%, transparent)' : undefined,
                    backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${HOUR_PX - 1}px, var(--border-subtle) ${HOUR_PX - 1}px, var(--border-subtle) ${HOUR_PX}px)`,
                  }}
                >
                  {(() => {
                    const dayAppts = forDay(d);
                    const lanes = lanesFor(dayAppts);
                    return dayAppts.map((a) => {
                      const pos = positionFor(a);
                      const { col, cols } = lanes.get(a.id) || { col: 0, cols: 1 };
                      return (
                        <ApptCard
                          key={a.id}
                          appt={a}
                          positioned
                          hideTime
                          consultantColor={colorFor(a)}
                          style={{
                            top: pos.top, height: pos.height,
                            left: `calc(${col} * 100% / ${cols} + 3px)`,
                            right: 'auto',
                            width: `calc(100% / ${cols} - 6px)`,
                            minWidth: 0,
                          }}
                          onClick={() => setSelected(a)}
                        />
                      );
                    });
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DAY view — any day, not only today */}
        {view === 'DAY' && (
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 600, fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
              <span>{rangeLabel}</span>
              <span className="mono text-xs text-muted">{forDay(anchor).length} appointment{forDay(anchor).length === 1 ? '' : 's'}</span>
            </div>
            {hours.map((h) => {
              const rowAppts = forDay(anchor).filter((a) => partsInOrgTz(a.startsAt)?.hour === h);
              return (
                <div key={h} style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', minHeight: 44 }}>
                  <div className="mono text-xs text-muted" style={{ width: 64, padding: '10px 10px', flexShrink: 0 }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                  <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap', padding: 6 }}>
                    {rowAppts.map((a) => (
                      <ApptCard key={a.id} appt={a} hideTime consultantColor={colorFor(a)} style={{ minWidth: 220 }} onClick={() => setSelected(a)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* RANGE view — agenda grouped by day, up to 31 days */}
        {view === 'RANGE' && (
          <div className="card" style={{ padding: 0 }}>
            {filtered.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="icon">📅</div>
                <div className="title">Nothing in this range</div>
                <div className="desc">Adjust the dates or filters, or book a new appointment.</div>
              </div>
            ) : days.map((d) => {
              const rows = forDay(d);
              if (!rows.length) return null;
              return (
                <div key={civilToISODate(d)} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{
                    padding: '8px 14px', fontSize: 12, fontWeight: 600,
                    color: isOrgToday(d) ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    background: 'var(--bg-glass)',
                  }}>
                    {formatInOrgTz(d, { weekday: 'long', day: 'numeric', month: 'long' })}
                    <span className="mono text-xs text-muted" style={{ marginLeft: 8 }}>{rows.length}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '8px 14px' }}>
                    {rows.map((a) => (
                      <ApptCard key={a.id} appt={a} consultantColor={colorFor(a)} style={{ minWidth: 240, flex: '1 1 240px', maxWidth: 360 }} onClick={() => setSelected(a)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* LIST view */}
        {view === 'LIST' && (
          <div className="card" style={{ padding: 0 }}>
            {filtered.length === 0 ? (
              <div className="empty-state" style={{ padding: 40 }}>
                <div className="icon">📅</div>
                <div className="title">No appointments in this period</div>
                <div className="desc">Adjust the filters or book a new appointment.</div>
              </div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>When</th><th>Type</th><th>Customer / Lead</th><th>Vehicle</th><th>Consultant</th><th>Location</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filtered]
                      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
                      .map((a) => {
                        const t = typeMeta(a.type);
                        return (
                          <tr key={a.id} onClick={() => setSelected(a)} style={{ cursor: 'pointer' }}>
                            <td>{fmtWhen(a.startsAt)} – {fmtTime(a.endsAt)}</td>
                            <td><span className="pill" style={{ borderColor: t.color, color: t.color }}>{t.icon} {t.label}</span></td>
                            <td>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {a._who || '—'}
                                <AiBadge source={a.source} compact style={{ fontSize: 10, padding: '0 5px' }} />
                              </span>
                            </td>
                            <td>{a._vehicle || '—'}</td>
                            <td>{a._consultant || '—'}</td>
                            <td>{a.location || '—'}</td>
                            <td>
                              <span className="pill filled" style={{ color: statusColor(a.status) }}>
                                {statusLabel(a.status)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ApptDrawer appt={selected} onClose={() => setSelected(null)} onChanged={refetch} />
      {/* Mounted only while open — the modal's lookup fields fetch consultants,
          customers and leads on mount, and there's no reason to pay for that
          on every visit to the diary. */}
      {modalOpen && (
        <NewAppointmentModal onClose={() => setModalOpen(false)} onCreated={refetch} />
      )}
    </>
  );
}
